/**
 *  @class
 *  @function HomeRatingPopup
 *  Opens the Trustpilot rating card below the rating bar.
 *  data-trigger="hover" opens on hover on devices with a mouse; touch devices always use click.
 */
if (!customElements.get('home-rating-popup')) {
  class HomeRatingPopup extends HTMLElement {
    constructor() {
      super();

      this.trigger = this.querySelector('[aria-controls]');
      this.panel = this.querySelector(`#${this.trigger.getAttribute('aria-controls')}`);
      this.hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.closeTimer = null;

      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeyup = this.onKeyup.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onBlockDeselect = this.onBlockDeselect.bind(this);

      this.trigger.addEventListener('click', this.toggle.bind(this));

      if (this.dataset.trigger === 'hover') {
        this.addEventListener('mouseenter', () => {
          if (!this.hoverMode) return;
          clearTimeout(this.closeTimer);
          this.open();
        });
        this.addEventListener('mouseleave', () => {
          if (!this.hoverMode) return;
          this.closeTimer = setTimeout(() => this.close(), 150);
        });
      }
    }

    get hoverMode() {
      return this.dataset.trigger === 'hover' && this.hoverQuery.matches;
    }

    connectedCallback() {
      if (!Shopify.designMode) return;
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    disconnectedCallback() {
      clearTimeout(this.closeTimer);
      this.close();
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    toggle() {
      // With hover enabled the popup is already open under the mouse, so a click must not close it.
      if (this.panel.hidden || this.hoverMode) {
        this.open();
      } else {
        this.close();
      }
    }

    open() {
      if (!this.panel.hidden) return;
      this.panel.hidden = false;
      this.trigger.setAttribute('aria-expanded', 'true');
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keyup', this.onKeyup);
    }

    close(returnFocus = false) {
      if (this.panel.hidden) return;
      this.panel.hidden = true;
      this.trigger.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keyup', this.onKeyup);
      if (returnFocus) this.trigger.focus();
    }

    onBlockSelect(event) {
      if (event.detail.blockId === this.dataset.blockId) this.open();
    }

    onBlockDeselect(event) {
      if (event.detail.blockId === this.dataset.blockId) this.close();
    }

    onDocumentClick(event) {
      if (!this.contains(event.target)) this.close();
    }

    onKeyup(event) {
      if (event.code.toUpperCase() === 'ESCAPE') this.close(true);
    }
  }

  customElements.define('home-rating-popup', HomeRatingPopup);
}
